import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { extensionOf, humanSize, inlineByteSize, typeLabel } from '../../../lib/artifact-file';
import { fileTypeKind } from '../../../lib/artifact-image';
import type { WorkspaceFile } from '../../../lib/workspace-files';
import { Card, Typography } from '../ui';
import { taskFileKey, type TaskFile } from '../../../lib/task-files';
import styles from './TaskFilesSection.module.css';

// Written-down exception to the one-file-explorer rule (repo-root CLAUDE.md): these are the files ONE task
// produced, read by origin across directories, so there is no root for FilesBrowser.
// "Files · N" (Figma "Single Task [2.0]"): a grid of the files the task produced, each the explorer's own
// type glyph on a tile, the name, and its type · size, opening the in-app doc viewer on click. Renders
// NOTHING when the task produced no file — no title, no empty state — matching native.
export function TaskFilesSection({ files, onOpen, className }: { files: TaskFile[]; onOpen: (file: TaskFile) => void; className?: string }) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  if (files.length === 0) return null;

  return (
    <section className={[styles.section, className].filter(Boolean).join(' ')} aria-label={t('files.title')}>
      <Typography variant="h4" component="h3">
        {`${t('files.title')} · ${files.length}`}
      </Typography>
      <div className={styles.grid}>
        {files.map((file) => {
          const item: WorkspaceFile = { name: file.name, isApp: false, extension: file.extension, content: file.content, referenceUrl: file.referenceUrl };
          const ext = extensionOf(item);
          const bytes = inlineByteSize(item);
          const meta = [typeLabel(ext), bytes != null ? humanSize(bytes) : null].filter(Boolean).join(' · ');
          return (
            <Card
              key={taskFileKey(file)}
              variant="sunken"
              size="sm"
              interactive
              component="button"
              className={styles.file}
              title={file.name}
              onClick={() => onOpen(file)}
            >
              <span className={styles.tile}>
                <img src={`${basePath}/file-types/${fileTypeKind(ext)}.png`} alt="" width={19} height={24} />
              </span>
              <span className={styles.text}>
                <Typography variant="p2" component="span" noWrap className={styles.name}>
                  {file.name}
                </Typography>
                {meta && <span className={styles.meta}>{meta}</span>}
              </span>
            </Card>
          );
        })}
      </div>
    </section>
  );
}
